/**
 * RESPONSIBILITY: draw the box of practical details beside an event.
 *
 * When it happens, where, and the two things somebody does next — find it, or put it in
 * their calendar. Every word on it is the Events section's, so the same box says the
 * same thing on every event page and an editor changes it once.
 *
 * THE DATE IS whenText's. This page already had one answer to "how is an event's date
 * written", and a second one here would be a second set of rules about ranges, notes and
 * all-day events — drifting from the moment either changed.
 */
import type { EventRow, EventsSection } from '@app/sections';
import { whenText } from '../commons/when';

/** Google's own template link. Their endpoint, their parameter names. */
function calendarUrl(e: EventRow): string {
  const stamp = (iso: string) => {
    const s = iso.replace(/[-:]/g, '');
    return s.length === 13 ? `${s}00` : s;
  };
  const from = e.startsAt ? stamp(e.startsAt) : '';
  const to = e.endsAt ? stamp(e.endsAt) : from;

  const q = new URLSearchParams({ action: 'TEMPLATE', text: e.title || '' });
  if (e.summary) q.set('details', e.summary);
  if (e.location) q.set('location', e.location);
  if (from) q.set('dates', `${from}/${to}`);
  return `https://calendar.google.com/calendar/render?${q}`;
}

const mapUrl = (e: EventRow): string =>
  e.ctaHref
  || (e.location ? `https://maps.google.com/?q=${encodeURIComponent(e.location)}` : '');

export function EventInfoPanel({ e, c }: { e: EventRow; c: EventsSection }) {
  const when = whenText(e);
  // The first line of an address is the venue; the rest is how to get to it.
  const [venue, ...rest] = (e.location ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const directions = mapUrl(e);

  if (!when && !venue) return null;

  return (
    <aside className="ev-info-panel">
      {c.panelHeading && (
        <p className="ev-info-header"><span className="ev-info-badge">{c.panelHeading}</span></p>
      )}

      {when && (
        <div className="ev-info-row">
          {c.panelWhenLabel && <h3 className="ev-info-label">{c.panelWhenLabel}</h3>}
          <p className="ev-info-value">{when}</p>
          {e.dateNote && <p className="ev-info-note">{e.dateNote}</p>}
        </div>
      )}

      {venue && (
        <div className="ev-info-row">
          {c.panelWhereLabel && <h3 className="ev-info-label">{c.panelWhereLabel}</h3>}
          <p className="ev-info-value">{venue}</p>
          {rest.length > 0 && <p className="ev-info-note">{rest.join(', ')}</p>}
        </div>
      )}

      {(directions || e.startsAt) && (
        <div className="ev-info-actions">
          {directions && c.panelDirectionsLabel && (
            <a className="btn btn-ghost" href={directions} target="_blank" rel="noopener noreferrer">
              {c.panelDirectionsLabel}
            </a>
          )}
          {e.startsAt && c.panelCalendarLabel && (
            <a className="btn btn-ghost" href={calendarUrl(e)} target="_blank" rel="noopener noreferrer">
              {c.panelCalendarLabel}
            </a>
          )}
        </div>
      )}
    </aside>
  );
}
